import * as React from 'react';
import { cn } from '../cn';
import { Skeleton } from './skeleton';

export interface FormSkeletonProps {
  /** Număr de secțiuni. Default 3. */
  sections?: number;
  /** Câmpuri per secțiune. Default 4. */
  fieldsPerSection?: number;
  /** Afișează un sidebar pe dreapta cu metadate placeholder. Default false. */
  showSidebar?: boolean;
  className?: string;
}

/**
 * Schelet pentru pagini de formular / detail. Stack de secțiuni cu titlu
 * (mai scurt) + câmpuri (label + input full-width). Spațierea respectă
 * gap-ul standard al app-ului (gap-3 between fields, gap-6 between sections).
 *
 * Folosit în: pagini detail (/facturi/[id], /clienti/[id]), modale de form,
 * panels în drawer-uri.
 */
export function FormSkeleton({
  sections = 3,
  fieldsPerSection = 4,
  showSidebar = false,
  className,
}: FormSkeletonProps) {
  const main = (
    <div className="space-y-7">
      {Array.from({ length: sections }).map((_, s) => (
        <div key={s} className="space-y-3">
          {/* Section title */}
          <Skeleton width={140 + (s % 3) * 30} height={14} rounded="sm" delay={s * 60} />
          <div className="rounded-lg border border-neutral-200 dark:border-neutral-800 p-5 space-y-4">
            {Array.from({ length: fieldsPerSection }).map((_, f) => (
              <div key={f} className="space-y-1.5">
                <Skeleton width={70 + ((s + f) % 4) * 25} height={10} rounded="sm" delay={s * 60 + f * 30} />
                <Skeleton width="100%" height={36} rounded="md" delay={s * 60 + f * 30 + 15} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  if (!showSidebar) {
    return (
      <div role="status" aria-label="Se încarcă..." className={cn('w-full', className)}>
        {main}
      </div>
    );
  }

  return (
    <div role="status" aria-label="Se încarcă..." className={cn('grid gap-6 grid-cols-1 lg:grid-cols-[1fr_280px]', className)}>
      {main}
      <aside className="space-y-4">
        <div className="rounded-lg border border-neutral-200 dark:border-neutral-800 p-4 space-y-3">
          <Skeleton width="60%" height={12} rounded="sm" />
          <Skeleton width="40%" height={20} rounded="md" />
          <Skeleton width="80%" height={10} rounded="sm" />
        </div>
        <div className="rounded-lg border border-neutral-200 dark:border-neutral-800 p-4 space-y-2">
          <Skeleton width="50%" height={12} rounded="sm" />
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} width="100%" height={28} rounded="md" delay={i * 40} />
          ))}
        </div>
      </aside>
    </div>
  );
}
